import constants from "./constants";

// REF: EVO-003 Phase 1 — Templated trial offers.
// This catalog pre-fills the trial-offer form with sensible defaults and surfaces
// a plain-English description plus a note about which behaviors are enforced by the
// Margot subscription service.
// REF: SPR-003 T-001 (Phase 2) — each preset now also carries an `offerType` token
// (the SDK enum NAME) so the chosen template is actually PERSISTED and Margot can
// apply its per-type eligibility gates. Presets also reset the per-type fields
// (campaign window / qualifying product / min seats / min tenure) to null so that
// switching templates never carries a stale typed field over; the admin fills the
// required one for the chosen type. See EVO-003 §3.1-§3.2, HANDOFF-003 §3/§6.5.

// Duration/discount preset values are sourced from the shared constants so they
// stay aligned to the SDK contract (TypeOfDuration / TypeOfDiscount).
const DURATION = constants.DURATION_TYPES.reduce(
  (map, d) => ({ ...map, [d.value]: d.value }),
  {},
);
const DISCOUNT = constants.DISCOUNT_TYPES.reduce(
  (map, d) => ({ ...map, [d.value]: d.value }),
  {},
);

// REF: SPR-003 T-001 — the SDK OfferType enum NAME tokens (sent verbatim; Margot
// parses by name, absent/empty/unknown ⇒ Custom / no gating). Exposed so the form
// and validation reference the same tokens as the templates.
export const OFFER_TYPES = {
  Entry: "Entry",
  Retention: "Retention",
  Reactivation: "Reactivation",
  Loyalty: "Loyalty",
  Seasonal: "Seasonal",
  CrossSell: "CrossSell",
  SeatExpansion: "SeatExpansion",
  Custom: "Custom",
};

// REF: SPR-003 T-001 — every preset resets the per-type fields to null so applying
// a template clears any stale typed field from a previously chosen template. The
// admin fills the field(s) required by the chosen type (enforced in offerInvalid).
// REF: SPR-004 T-001 — also resets DiscountMode to SingleTerm (+ RecurringPeriods
// to null) on every template application/new offer, so a RecurringPerPeriod choice
// on one offer never bleeds into the next template picked — no preset defaults to
// recurring; admins opt in per-offer (HANDOFF-004 §4).
export const TYPED_FIELD_DEFAULTS = {
  campaignStartsAt: null,
  campaignEndsAt: null,
  qualifyingProductId: null,
  minSeatQuantity: null,
  minTenureDays: null,
  discountMode: constants.DISCOUNT_MODES[0].value,
  recurringPeriods: null,
};

// Preset for the "Custom" template — mirrors createNewOffer()'s manual defaults.
const CUSTOM_PRESET = {
  name: "",
  price: 0,
  durationCount: 7,
  durationType: DURATION.Days,
  daysSinceLastOffer: 365,
  discountType: null,
  discountPercent: null,
  offerType: OFFER_TYPES.Custom,
  ...TYPED_FIELD_DEFAULTS,
};

// Default template id — blank / today's manual behavior.
export const DEFAULT_OFFER_TEMPLATE_ID = "custom";

// The eight Human-confirmed templates (EVO-003 §3.1). Each: { id, label,
// description, margotBehaviorNote, preset }. `preset` mirrors an offer's editable
// fields; the admin can still edit any field after applying a template.
const OFFER_TEMPLATES = [
  {
    id: "entry",
    label: "Entry / Welcome Offer",
    description:
      "An introductory offer for first-time buyers to encourage their first purchase.",
    margotBehaviorNote:
      "First-purchase eligibility is enforced by the subscription service.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.Entry,
      name: "Welcome Offer",
      price: 0,
      durationCount: 14,
      durationType: DURATION.Days,
      daysSinceLastOffer: 365,
      discountType: DISCOUNT.Percentage,
      discountPercent: 50,
    },
  },
  {
    id: "retention",
    label: "Retention / Save-at-Cancel",
    description:
      "A save offer shown when a user is trying to cancel, to keep them subscribed.",
    margotBehaviorNote:
      "Triggered at cancellation by the subscription service (not yet wired — Phase 2).",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.Retention,
      name: "Stay With Us",
      price: 0,
      durationCount: 3,
      durationType: DURATION.Months,
      daysSinceLastOffer: 0,
      discountType: DISCOUNT.Percentage,
      discountPercent: 40,
    },
  },
  {
    id: "reactivation",
    label: "Reactivation / Win-back",
    description:
      "A win-back offer aimed at lapsed or expired users to bring them back.",
    margotBehaviorNote:
      "Lapsed-user eligibility is enforced by the subscription service.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.Reactivation,
      name: "Welcome Back",
      price: 0,
      durationCount: 1,
      durationType: DURATION.Months,
      daysSinceLastOffer: 90,
      discountType: DISCOUNT.Percentage,
      discountPercent: 30,
    },
  },
  {
    id: "loyalty",
    label: "Loyalty / Renewal Reward",
    description:
      "A reward for tenured, active subscribers, typically applied at renewal.",
    margotBehaviorNote:
      "Tenure/renewal gating is enforced by the subscription service.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.Loyalty,
      // Sensible default: reward subscribers with ≥ 1 year of active tenure. The
      // admin can adjust; Margot gates on active tenure ≥ minTenureDays.
      minTenureDays: 365,
      name: "Loyalty Reward",
      price: 0,
      durationCount: 1,
      durationType: DURATION.Years,
      daysSinceLastOffer: 365,
      discountType: DISCOUNT.Percentage,
      discountPercent: 15,
    },
  },
  {
    id: "seasonal",
    label: "Seasonal / Promotional",
    description:
      "A time-boxed promotional offer available to everyone during a campaign.",
    margotBehaviorNote:
      "Campaign date-window gating is a Phase-2 addition.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.Seasonal,
      // Campaign window is admin-set (null bound = unbounded); left null to fill.
      name: "Seasonal Promo",
      price: 0,
      durationCount: 1,
      durationType: DURATION.Months,
      daysSinceLastOffer: 0,
      discountType: DISCOUNT.Percentage,
      discountPercent: 25,
    },
  },
  {
    id: "crosssell",
    label: "Upgrade / Cross-sell",
    description:
      "A discounted trial offered to customers who own a related product.",
    margotBehaviorNote:
      "Owned-product eligibility is enforced by the subscription service.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.CrossSell,
      // qualifyingProductId is REQUIRED for CrossSell (offerInvalid blocks save);
      // left null for the admin to select the owned product that qualifies.
      name: "Upgrade Offer",
      price: 0,
      durationCount: 1,
      durationType: DURATION.Months,
      daysSinceLastOffer: 0,
      discountType: DISCOUNT.Percentage,
      discountPercent: 25,
    },
  },
  {
    id: "seat",
    label: "Seat-expansion / Volume",
    description:
      "A volume offer for team plans that are adding additional seats.",
    margotBehaviorNote:
      "Seat-threshold gating is enforced by the subscription service.",
    preset: {
      ...TYPED_FIELD_DEFAULTS,
      offerType: OFFER_TYPES.SeatExpansion,
      // minSeatQuantity is REQUIRED for SeatExpansion (offerInvalid blocks save);
      // left null for the admin to set the qualifying seat threshold.
      name: "Team Volume Offer",
      price: 0,
      durationCount: 1,
      durationType: DURATION.Years,
      daysSinceLastOffer: 0,
      discountType: DISCOUNT.Fixed,
      discountPercent: 0,
    },
  },
  {
    id: DEFAULT_OFFER_TEMPLATE_ID,
    label: "Custom (no template)",
    description:
      "Full manual control — set every field yourself. This is the default.",
    margotBehaviorNote: "No automatic behavior.",
    preset: { ...CUSTOM_PRESET },
  },
];

export function getOfferTemplate(id) {
  return (
    OFFER_TEMPLATES.find((t) => t.id === id) ||
    OFFER_TEMPLATES.find((t) => t.id === DEFAULT_OFFER_TEMPLATE_ID)
  );
}

export default OFFER_TEMPLATES;
